import { RiEmotionLine } from '@remixicon/react';
import classNames from 'classnames';

import { Button, Flex } from '@spotify-internal/encore-web';
import { Tooltip, TooltipRoot } from '@spotify-internal/encore-web/next';
import {
  ReactionSummaryFragment,
  getTypeOrNull,
} from '@spotify/confidence-graphql';

import { EmojiPickerPopover } from '../EmojiPickerPopover';

import styles from './EmojiReactions.module.css';

export type EmojiReaction = ReactionSummaryFragment['emojiReactions'][number];

export interface EmojiReactionsProps {
  emojiReactions: EmojiReaction[];
  userReactedEmojis?: Set<string>;
  compact?: boolean;
  onToggleReaction?: (emoji: string, isAdding: boolean) => void;
}

const EmojiReactions = ({
  emojiReactions,
  userReactedEmojis = new Set(),
  compact = true,
  onToggleReaction,
}: EmojiReactionsProps) => {
  const handleEmojiSelect = (emoji: string) => {
    if (userReactedEmojis.has(emoji)) return;
    onToggleReaction?.(emoji, true);
  };

  return (
    <Flex alignItems="center" gap="tighter-4" wrap="wrap">
      {emojiReactions.map(reaction => {
        const emoji = reaction.value?.emoji ?? reaction.key;
        const count = reaction.value?.reactionCount ?? 0;
        const isActive = userReactedEmojis.has(emoji);

        const identityNames = (reaction.value?.identities ?? [])
          .map(identity => getTypeOrNull(identity)?.displayName)
          .filter(Boolean)
          .join(', ');

        return (
          <TooltipRoot key={reaction.key}>
            <Button
              variant={isActive ? 'secondary-bordered' : 'tertiary'}
              size="small"
              aria-pressed={isActive}
              onClick={() => onToggleReaction?.(emoji, !isActive)}
              data-track-id="button.reaction"
              data-track-action="update"
            >
              <span
                className={classNames(styles.emoji, {
                  [styles.compactEmoji]: compact,
                })}
              >
                {emoji}
              </span>
              {count}
            </Button>
            <Tooltip>{identityNames || reaction.key}</Tooltip>
          </TooltipRoot>
        );
      })}
      <EmojiPickerPopover
        icon={<RiEmotionLine size={16} />}
        onEmojiSelect={handleEmojiSelect}
      />
    </Flex>
  );
};

export default EmojiReactions;
